import React from 'react';
import { Popover as AntPopover } from 'antd';
import type { TooltipPlacement } from 'antd/es/tooltip';

import { cx, type Styleable } from '../../variants';

export interface HoverCardProps extends Styleable {
    /** The element that triggers the card on hover, e.g. an `Item` row. */
    trigger: React.ReactNode;
    /** The rich preview body — e.g. an avatar plus a couple of key/value lines. */
    content: React.ReactNode;
    placement?: TooltipPlacement;
    /** Controlled open state — omit for antd's own hover-managed state. */
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    /** Renders `trigger` with no popover at all — the row has nothing to preview. */
    disabled?: boolean;
}

/**
 * EAA-1804: thin wrapper over antd `Popover`, not a reimplementation — antd
 * already themes the overlay via `createAntdTheme`, so there is nothing left
 * to re-derive here except the one thing every ad-hoc "hover to preview" call
 * site was guessing independently: timing. `mouseEnterDelay={0.3}` is long
 * enough that a fast mouse-pass over a table row does not flash the card
 * open; `mouseLeaveDelay={0.1}` is short enough that moving off still feels
 * responsive rather than sticky. One shared pair beats N per-call-site
 * guesses drifting apart.
 *
 * `trigger` is wrapped in a plain `<span>` rather than passed to `Popover`
 * directly: antd/rc-trigger clones its child to attach hover handlers, which
 * only reaches the actual DOM node for elements that forward refs and spread
 * unknown props. `Item` and most sheet primitives do neither (they take a
 * fixed prop set), so an un-wrapped `Item` trigger would silently never open.
 * `display: 'block'` keeps a full-width trigger (like `Item`) filling its
 * container instead of shrinking to an inline box.
 */
export function HoverCard({
    trigger,
    content,
    placement = 'bottom',
    open,
    onOpenChange,
    disabled = false,
    className,
    style,
}: HoverCardProps): React.ReactElement {
    const wrappedTrigger = (
        <span
            className={cx('ui-hover-card-trigger', className)}
            style={{ display: 'block', ...style }}
        >
            {trigger}
        </span>
    );

    if (disabled) return wrappedTrigger;

    return (
        <AntPopover
            content={content}
            placement={placement}
            trigger="hover"
            mouseEnterDelay={0.3}
            mouseLeaveDelay={0.1}
            open={open}
            onOpenChange={onOpenChange}
            // antd v6 semantic styling key for the content wrapper (the old
            // `overlayInnerStyle`) — gives every card the same min-width/padding
            // instead of each call site reinventing card padding.
            styles={{ content: { minWidth: 220, padding: 12 } }}
        >
            {wrappedTrigger}
        </AntPopover>
    );
}

export default HoverCard;
